/* eslint-disable jsx-a11y/aria-role */
import { IconPlus, IconTrashFilled } from '@iwoca/icons';
import { Button, Input } from '@iwoca/orion';
import clsx from 'clsx';
import { useIntroducerData } from 'contexts/IntroducerData/IntroducerData.context';
import { useAmplitude } from 'hooks/useAmplitude';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { useSignupData } from '../hooks/useSignupData';
import { PersonalDetails } from '../PersonalDetails/PersonalDetails';
import { TSignupFormData } from '../SignupForm/SignupForm.types';

import { DisabledGuarantors } from './DisabledGuarantors/DisabledGuarantors';
import { GuarantorRoles } from './GuarantorRoles/GuarantorRoles';
import styles from './Guarantors.module.css';

export const Guarantors = () => {
  const { trackEvent } = useAmplitude();
  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useFormContext<TSignupFormData>();

  const watchPrivacyPolicy = watch('applicant.privacyPolicyAgreement');
  const { checkIntroducerPermission } = useIntroducerData();
  const { isReintroduction } = useSignupData();
  const { fields, append, remove } = useFieldArray({ name: 'guarantors' });

  const setPrivacyPolicy = (index: number) => {
    setValue(`guarantors.${index}.privacyPolicyAgreement`, watchPrivacyPolicy);
  };

  if (isReintroduction) {
    return <DisabledGuarantors />;
  }

  return (
    <div
      className={clsx(
        styles.container,
        fields.length === 0 && styles.buttonContainer,
      )}
    >
      {fields.map((field, index) => {
        setPrivacyPolicy(index);

        return (
          <section key={field.id} className={styles.fieldContainer}>
            {index > 0 && <hr className={styles.seperator} />}
            <div className={styles.guarantorHeading}>
              <h2>Contact {index + 1}</h2>
              <Button
                type="button"
                data-testid="remove-guarantor-button"
                variant="tertiary"
                className={clsx(styles.guarantorButton, styles.removeGuarantor)}
                onClick={() => {
                  remove(index);
                  trackEvent(AMPLITUDE_EVENTS.extraDetailsRemoved, {
                    'Details added': 'New contact',
                    'CTA text': 'Remove',
                  });
                }}
              >
                <IconTrashFilled />
              </Button>
            </div>
            <GuarantorRoles index={index} />
            <div className={styles.nameContainer}>
              <Input
                {...register(`guarantors.${index}.firstName`)}
                type="text"
                label="First name"
                wrapperClassName={styles.nameField}
                error={errors?.guarantors?.[index]?.firstName?.message}
              />
              <Input
                {...register(`guarantors.${index}.lastName`)}
                type="text"
                label="Last name"
                wrapperClassName={styles.nameField}
                error={errors?.guarantors?.[index]?.lastName?.message}
              />
            </div>
            <PersonalDetails role="guarantor" index={index} />
          </section>
        );
      })}
      <Button
        type="button"
        variant="tertiary"
        className={clsx(styles.guarantorButton, styles.addGuarantor)}
        disabled={!checkIntroducerPermission('can_create_applications')}
        onClick={() => {
          append({});
          trackEvent(AMPLITUDE_EVENTS.extraDetailsAdded, {
            'Details added': 'New contact',
            'CTA text': 'Add new contact',
          });
        }}
      >
        <IconPlus fontSize="1.3rem" />
        Add new contact
      </Button>
    </div>
  );
};
